import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowRight, Phone } from "lucide-react";
import { HERO_IMG } from "../../data/site";

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.25 + i * 0.12 },
  }),
};

const Line = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      custom={i}
      variants={lineVariant}
      initial="hidden"
      animate="show"
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  const go = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    lenis ? lenis.scrollTo(el, { offset: -70 }) : el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-end overflow-hidden bg-[#0A0A0A]"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={HERO_IMG}
          alt="Officina professionale Autofficina Ciro Natale"
          className="h-full w-full object-cover"
        />
        <motion.div
          style={{ opacity: overlayOpacity }}
          className="absolute inset-0 bg-black"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/60" />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-[1400px] px-5 pb-16 pt-32 md:px-10 md:pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="mb-6 flex items-center gap-3 font-mono-accent text-[11px] uppercase tracking-[0.3em] text-[#FF2A2A]"
        >
          <span className="h-px w-10 bg-[#FF2A2A]" />
          Esperienza, tecnologia e passione dal 1972
        </motion.p>

        <h1
          data-testid="hero-title"
          className="font-display text-[15vw] font-bold uppercase leading-[0.85] tracking-tighter text-[#FAFAFA] md:text-[9vw]"
        >
          <Line i={0}>Autofficina</Line>
          <Line i={1} className="text-[#FF2A2A]">
            Ciro Natale
          </Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-xl font-body text-base leading-relaxed text-[#A1A1AA] md:text-lg"
        >
          Da più di 50 anni ci prendiamo cura dei vostri veicoli offrendo servizi
          di manutenzione, riparazione e revisione con professionalità e
          competenza.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row"
        >
          <button
            data-testid="hero-cta-prenota"
            onClick={() => go("contatti")}
            className="group flex items-center justify-center gap-3 bg-[#FF2A2A] px-8 py-4 font-display text-sm font-semibold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-white hover:text-black"
          >
            Prenota un appuntamento
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-cta-contatti"
            onClick={() => go("contatti")}
            className="flex items-center justify-center gap-3 border border-white/20 px-8 py-4 font-display text-sm font-semibold uppercase tracking-wide text-white transition-colors duration-300 hover:border-white hover:bg-white/5"
          >
            <Phone className="h-4 w-4" />
            Contattaci
          </button>
        </motion.div>
      </div>
    </section>
  );
};
